import type { ContributorDto } from '@/types/dtos/contributors/response/contributor.dto';
import type { FlowDto } from '@/types/dtos/flows/response/flow.dto';

const normalizeId = (value?: unknown): string | undefined => {
  if (!value) return undefined;
  if (typeof value === 'string') return value;
  if (typeof value === 'object') {
    if ('id' in value && typeof (value as { id?: string }).id === 'string') {
      return (value as { id: string }).id;
    }
    if ('_id' in value && typeof (value as { _id?: string })._id === 'string') {
      return (value as { _id: string })._id;
    }
    if ('$oid' in value && typeof (value as { $oid?: string }).$oid === 'string') {
      return (value as { $oid: string }).$oid;
    }
  }
  return undefined;
};

/** Flow-tab contributor visibility (multi-flow slot scoping); matches `ContributorsList` with `flowContext`. */
export function filterContributorsForFlowContext(
  sortedContributors: ContributorDto[],
  flowContext: FlowDto | undefined,
  eventMultiFlow: boolean | undefined,
): ContributorDto[] {
  if (!flowContext) {
    return sortedContributors;
  }
  if (eventMultiFlow === false) {
    return sortedContributors;
  }

  const flowIds = new Set<string>(
    [normalizeId(flowContext.id), normalizeId(flowContext.origin)].filter(Boolean) as string[],
  );
  const flowEventSlotIds = new Set<string>(
    [normalizeId(flowContext.slot?.id), normalizeId(flowContext.slot?.origin)].filter(Boolean) as string[],
  );

  const flowSlotIds = flowContext.slots
    ? new Set(flowContext.slots.map((slot) => normalizeId(slot.id)).filter(Boolean) as string[])
    : new Set<string>();

  const matchesEventSlot = (eventSlotId?: string) => {
    if (flowEventSlotIds.size === 0) return true;
    const normalizedEventSlotId = normalizeId(eventSlotId);
    if (!normalizedEventSlotId) return false;
    return flowEventSlotIds.has(normalizedEventSlotId);
  };

  return sortedContributors.filter((contributor) => {
    const hasFlowRole = contributor.roles.some((role) => {
      if (role.entityType !== 'Flow') return false;
      const roleFlowIds = new Set<string>();
      const directId = normalizeId(role.entity);
      if (directId) roleFlowIds.add(directId);
      if (typeof role.entity === 'object' && role.entity) {
        const originId = normalizeId((role.entity as { origin?: unknown }).origin);
        if (originId) roleFlowIds.add(originId);
      }
      const matchesFlow = Array.from(roleFlowIds).some((id) => flowIds.has(id));
      if (!matchesFlow) return false;
      if (flowEventSlotIds.size === 0) return true;
      return role.slots?.some((slot) => matchesEventSlot(slot.eventSlotId)) ?? false;
    });

    if (hasFlowRole) return true;

    const sessionRoles = contributor.roles.filter((r) => r.entityType === 'Session');

    return sessionRoles.some((role) =>
      role.slots?.some((slot) => {
        if (slot.entityType !== 'FlowSlot' || slot.deleted === true) {
          return false;
        }
        const slotEntityId = normalizeId(slot.entity);
        if (!slotEntityId || !flowSlotIds.has(slotEntityId)) return false;
        return matchesEventSlot(slot.eventSlotId);
      }),
    );
  });
}
